import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box, Chip, Stack, Typography } from '@mui/material';
import React from 'react';

import type { MarketplaceService } from '../../types/pricing';

function fieldLabel(field: any): string {
  return field?.label || field?.name || field?.key || 'Order detail';
}

function fieldReason(field: any): string | null {
  return typeof field?.reason === 'string' && field.reason.trim() ? field.reason.trim() : null;
}

function modeLabel(mode?: string): string {
  switch (mode) {
    case 'transactional':
      return 'Orderable';
    case 'configurable':
      return 'Configurable order';
    case 'guided_checkout':
      return 'Guided checkout';
    case 'manual_finalize':
      return 'Finalize after checkout';
    case 'reference_only':
      return 'Reference / preference item';
    default:
      return 'Order schema';
  }
}

const OrderSchemaPanel: React.FC<{ service: MarketplaceService }> = ({ service }) => {
  const schema = (service as any)?.order_schema;
  if (!schema || typeof schema !== 'object') return null;

  const preCheckout = Array.isArray(schema.pre_checkout_fields) ? schema.pre_checkout_fields : [];
  const checkout = Array.isArray(schema.checkout_required_fields) ? schema.checkout_required_fields : [];
  const finalization = Array.isArray(schema.post_checkout_finalization_fields) ? schema.post_checkout_finalization_fields : [];
  const isReference = schema.mode === 'reference_only';

  if (!preCheckout.length && !checkout.length && !finalization.length && !isReference) return null;

  const finalizationCopy = isReference
    ? 'This is not a standalone cart item. Add it as a preference to an eligible service order; exact model, financing, trade-in, and inventory are confirmed during finalization.'
    : 'Some details may be finalized after checkout before supplier submission. Aithon keeps that work attached to the order instead of hiding it in email.';

  return (
    <Box sx={{ mt: 3, p: 2.25, border: '1px solid', borderColor: isReference ? 'warning.light' : 'divider', borderRadius: 2, bgcolor: isReference ? 'warning.50' : 'background.paper' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        {isReference ? <InfoOutlined color="warning" fontSize="small" /> : <CheckCircleOutline color="primary" fontSize="small" />}
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          What we need to start
        </Typography>
        <Chip size="small" label={modeLabel(schema.mode)} color={isReference ? 'warning' : 'primary'} variant="outlined" />
      </Stack>

      {preCheckout.length > 0 && (
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Before cart</Typography>
          <Stack spacing={0.5}>
            {preCheckout.map((field: any, index: number) => (
              <Typography key={`${field?.key || 'pre'}-${index}`} variant="body2" color="text.secondary">
                • {fieldLabel(field)}{field?.required ? ' — required' : ''}{field?.default ? ` · default: ${field.default}` : ''}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {checkout.length > 0 && (
        <Box sx={{ mb: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>At checkout</Typography>
          <Stack spacing={0.5}>
            {checkout.map((field: any, index: number) => (
              <Typography key={`${field?.key || 'checkout'}-${index}`} variant="body2" color="text.secondary">
                • {fieldLabel(field)}{field?.required ? ' — required' : ''}{fieldReason(field) ? ` · ${fieldReason(field)}` : ''}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {(finalization.length > 0 || isReference) && (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>After checkout / finalization</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: finalization.length ? 0.5 : 0 }}>
            {finalizationCopy}
          </Typography>
          {finalization.length > 0 && (
            <Stack spacing={0.5}>
              {finalization.map((field: any, index: number) => (
                <Typography key={`${field?.key || 'final'}-${index}`} variant="body2" color="text.secondary">
                  • {fieldLabel(field)}{field?.required ? ' — required' : ''}{fieldReason(field) ? ` · ${fieldReason(field)}` : ''}
                </Typography>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(OrderSchemaPanel);
